import { lazy, ComponentType } from "react";

/**
 * Wrapper for dynamic imports that handles chunk loading failures after deployments.
 * When a new deployment changes chunk hashes, users with cached app may try to load
 * old chunks that no longer exist. This catches those errors and reloads the page.
 */
function lazyWithRetry<T extends ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(() =>
    importFn().catch((error: Error) => {
      // Check if this is a chunk loading error
      const isChunkError =
        error.message.includes("Failed to fetch dynamically imported module") ||
        error.message.includes("Unable to preload CSS") ||
        error.message.includes("Loading chunk") ||
        error.message.includes("Loading CSS chunk") ||
        error.name === "ChunkLoadError";

      if (isChunkError) {
        console.warn("[ChunkLoad] Asset loading failed, reloading page for fresh assets...", error);

        // Store flag to prevent infinite reload loops
        const reloadKey = "chunk_reload_" + window.location.pathname;
        const lastReload = sessionStorage.getItem(reloadKey);
        const now = Date.now();

        // Only reload if we haven't reloaded in the last 10 seconds
        if (!lastReload || now - parseInt(lastReload) > 10000) {
          sessionStorage.setItem(reloadKey, now.toString());
          window.location.reload();
        }
      }

      // Re-throw so React error boundary can handle it
      throw error;
    }),
  );
}

export const Home = lazy(() => import("../components/home"));

export const Dashboard = lazyWithRetry(() =>
  import("../views/home/dashboard/SkyAdvisorDashboard").then((module) => ({
    default: module.SkyAdvisorDashboard,
  })),
);

export const Apps = lazy(() => import("../components/apps"));

export const HelpPlusSupport = lazy(() => import("../views/helpPlusSupport/helpPlusSupport"));

export const QueryAndIssues = lazy(
  () => import("../views/helpPlusSupport/queryAndIssues/queryAndIssues"),
);

export const Platform = lazy(() => import("../views/settings/platform/platform"));

export const Account = lazy(() => import("../views/account/account"));

export const InboxPage = lazyWithRetry(() =>
  import("../views/inbox/ChatInboxPage").then((module) => ({
    default: module.ChatInboxPage,
  })),
);

export const AnalyticsDashboardPage = lazyWithRetry(() =>
  import("../views/analytics/AnalyticsDashboard").then((module) => ({
    default: module.AnalyticsDashboard,
  })),
);

export const CallAnalyticsDashboardPage = lazy(() =>
  import("../views/analytics/CallAnalyticsDashboard").then((module) => ({
    default: module.CallAnalyticsDashboard,
  })),
);

export const UtmAnalyticsDashboardPage = lazy(
  () => import("../views/analytics/UtmAnalyticsDashboard"),
);

export const CampaignAttributionDashboardPage = lazy(
  () => import("../views/analytics/CampaignAttributionDashboard"),
);

export const ModalConfigsPage = lazy(() => import("../views/asa-modal/ModalConfigsPage"));

export const EmbedScriptGeneratorPage = lazy(
  () => import("../views/asa-modal/EmbedScriptGenerator"),
);

export const TourAvailabilityPage = lazy(() => import("../views/asa-modal/TourAvailabilityPage"));

export const StaffProfilesPage = lazy(() => import("../views/asa-modal/StaffProfilesPage"));

export const SmsAlertSettingsPage = lazy(() => import("../views/asa-modal/SmsAlertSettingsPage"));

export const NotificationPreferencesPage = lazy(
  () => import("../views/settings/notifications/NotificationPreferencesPage"),
);

export const AlertBannersPage = lazy(() => import("../views/asa-modal/AlertBannersPage"));

export const VideosPage = lazy(() => import("../views/asa-modal/VideosPage"));

export const FaqPage = lazy(() => import("../views/asa-modal/FaqPage"));

export const SurveysPage = lazy(() =>
  import("../views/surveys/SurveysPage").then((module) => ({
    default: module.SurveysPage,
  })),
);

export const SurveyBuilderPage = lazy(() =>
  import("../views/surveys/builder/SurveyBuilderPage").then((module) => ({
    default: module.SurveyBuilderPage,
  })),
);

export const SurveyDetailPage = lazy(() =>
  import("../views/surveys/detail/SurveyDetailPage").then((module) => ({
    default: module.SurveyDetailPage,
  })),
);

export const SurveyPreviewPage = lazy(() =>
  import("../views/surveys/preview/SurveyPreviewPage").then((module) => ({
    default: module.SurveyPreviewPage,
  })),
);

export const SurveyTemplatesPage = lazy(() =>
  import("../views/surveys/templates/SurveyTemplatesPage").then((module) => ({
    default: module.SurveyTemplatesPage,
  })),
);

export const PublicSurveyPage = lazy(() =>
  import("../views/surveys/public/PublicSurveyPage").then((module) => ({
    default: module.PublicSurveyPage,
  })),
);

export const AssessmentsListPage = lazy(() =>
  import("../views/assessments/AssessmentsListPage").then((module) => ({
    default: module.AssessmentsListPage,
  })),
);

export const PublicAssessmentPage = lazy(() =>
  import("../views/assessments/public/PublicAssessmentPage").then((module) => ({
    default: module.PublicAssessmentPage,
  })),
);

export const CampaignsListPage = lazy(() =>
  import("../views/campaigns/CampaignsListPage").then((module) => ({
    default: module.CampaignsListPage,
  })),
);

export const CampaignBuilderPage = lazy(() =>
  import("../views/campaigns/CampaignBuilderPage").then((module) => ({
    default: module.CampaignBuilderPage,
  })),
);

export const AdCampaignsPage = lazy(() =>
  import("../views/campaigns/CampaignsPage").then((module) => ({
    default: module.CampaignsPage,
  })),
);

export const DripCampaignListPage = lazy(
  () => import("../views/drip-campaigns/DripCampaignListPage"),
);
export const DripCampaignDetailPage = lazy(
  () => import("../views/drip-campaigns/DripCampaignDetailPage"),
);
export const DripCampaignEditorPage = lazy(
  () => import("../views/drip-campaigns/DripCampaignEditorPage"),
);

export const ConfigurationsPage = lazy(
  () => import("../views/settings/configurations/ConfigurationsPage"),
);

export const SKYPOINTSIGNUP = lazy(() => import("../components/skypointsignup"));

export const InstancesParent = lazy(
  () => import("../views/settings/instances/instances-parent/instancesParent"),
);

export const InstanceHistory = lazy(
  () => import("../views/settings/instances/instanceHistory/instanceHistory"),
);

export const InstancesDetails = lazy(
  () => import("../views/settings/instances/instancesDetails/instancesDetails"),
);

export const InstanceBackup = lazy(
  () => import("../views/settings/instances/instanceBackup/instanceBackup"),
);

export const TenantsSettings = lazy(() => import("../views/settings/tenants/tenants"));

export const CreateTenant = lazy(
  () => import("../views/settings/tenants/createTenant/createTenant"),
);

export const LogsPage = lazy(() => import("../views/settings/logs/LogsPage"));

export const CrmUserDirectoryPage = lazy(
  () => import("../views/settings/users/CrmUserDirectoryPage"),
);
export const UserSetupRedirect = lazy(() => import("../views/settings/users/UserSetupRedirect"));

export const RoleManagementPage = lazy(() => import("../views/settings/roles/RoleManagementPage"));

export const ErrorPage = lazy(() => import("../components/shared/errorpage"));

export const GeneralSettingsPage = lazy(() =>
  import("../views/settings/general/GeneralSettingsPage").then((module) => ({
    default: module.GeneralSettingsPage,
  })),
);

export const AsaListPage = lazyWithRetry(() =>
  import("../views/settings/asa/AsaListPage").then((module) => ({
    default: module.AsaListPage,
  })),
);

export const AsaEditPage = lazyWithRetry(() =>
  import("../views/settings/asa/AsaEditPage").then((module) => ({
    default: module.AsaEditPage,
  })),
);

export const AsaLocationsPage = lazyWithRetry(() =>
  import("../views/settings/asa/AsaLocationsPage").then((module) => ({
    default: module.AsaLocationsPage,
  })),
);

export const ChannelsPage = lazy(() =>
  import("../views/settings/channels/ChannelsPage").then((module) => ({
    default: module.ChannelsPage,
  })),
);

export const CallsPage = lazy(() =>
  import("../views/settings/calls/CallsPage").then((module) => ({
    default: module.CallsPage,
  })),
);

export const TrainingPage = lazy(() =>
  import("../views/training/TrainingPage").then((module) => ({
    default: module.TrainingPage,
  })),
);

export const LeadsPage = lazyWithRetry(() =>
  import("../views/leads/LeadsPage").then((module) => ({
    default: module.LeadsPage,
  })),
);

export const ScheduledToursPage = lazy(() =>
  import("../views/scheduled-tours/ScheduledToursPage").then((module) => ({
    default: module.ScheduledToursPage,
  })),
);

export const LeadDetailPage = lazy(() =>
  import("../views/leads/LeadDetailPage").then((module) => ({
    default: module.LeadDetailPage,
  })),
);

export const CrmIntegrationsPage = lazy(() =>
  import("../views/settings/CrmIntegrationsPage").then((module) => ({
    default: module.CrmIntegrationsPage,
  })),
);

export const CallManagementPage = lazy(() =>
  import("../views/settings/callManagement").then((m) => ({ default: m.CallManagementPage })),
);

// Sky CRM (/crm/*) — pack-gated placeholder pages (Day 4 scaffold).
// Default exports, so each module is already { default: Component }.
export const CrmDayPlannerPage = lazyWithRetry(() =>
  import("../views/crm/DayPlannerPage").then((m) => ({ default: m.default })),
);
export const CrmOverviewPage = lazyWithRetry(() =>
  import("../views/crm/OverviewPage").then((m) => ({ default: m.default })),
);
export const CrmFalloutPage = lazyWithRetry(() =>
  import("../views/crm/FalloutPage").then((m) => ({ default: m.default })),
);
export const CrmTodayPage = lazyWithRetry(() =>
  import("../views/crm/TodayPage").then((m) => ({ default: m.default })),
);
export const CrmSkyAgentPage = lazyWithRetry(() =>
  import("../views/crm/skyagent/SkyAgentPage").then((m) => ({ default: m.default })),
);
export const CrmSkyBrainPage = lazyWithRetry(() =>
  import("../views/crm/skyagent/BrainPage").then((m) => ({ default: m.default })),
);
export const CrmAgentSkillsPage = lazyWithRetry(() =>
  import("../views/crm/skyagent/SkillsPage").then((m) => ({ default: m.default })),
);
export const CrmMcpConnectorsPage = lazyWithRetry(() =>
  import("../views/crm/skyagent/ConnectorsPage").then((m) => ({ default: m.default })),
);
export const CrmAgentDraftsPage = lazyWithRetry(() =>
  import("../views/crm/skyagent/DraftsPage").then((m) => ({ default: m.default })),
);
export const CrmLandingRedirect = lazyWithRetry(() =>
  import("../views/crm/CrmLandingRedirect").then((m) => ({ default: m.default })),
);
export const CrmProspectsPage = lazyWithRetry(() =>
  import("../views/crm/ProspectsPage").then((m) => ({ default: m.default })),
);
export const CrmMessengerPage = lazyWithRetry(() =>
  import("../views/crm/MessengerPage").then((m) => ({ default: m.default })),
);
export const CrmActivitiesPage = lazyWithRetry(() =>
  import("../views/crm/ActivitiesPage").then((m) => ({ default: m.default })),
);
export const CrmResidentsPage = lazyWithRetry(() =>
  import("../views/crm/ResidentsPage").then((m) => ({ default: m.default })),
);
export const CrmOccupancyPage = lazyWithRetry(() =>
  import("../views/crm/OccupancyPage").then((m) => ({ default: m.default })),
);
export const CrmOccupancyBoardPage = lazyWithRetry(() =>
  import("../views/crm/OccupancyBoardPage").then((m) => ({ default: m.default })),
);
export const CrmAdmissionsBoardPage = lazyWithRetry(() =>
  import("../views/crm/AdmissionsBoardPage").then((m) => ({ default: m.default })),
);
export const CrmOccupancyUnitsPage = lazyWithRetry(() =>
  import("../views/crm/UnitsPage").then((m) => ({ default: m.default })),
);
export const CrmOccupancyReportPage = lazyWithRetry(() =>
  import("../views/crm/OccupancyReportPage").then((m) => ({ default: m.default })),
);
export const CrmOccupancyWaitlistPage = lazyWithRetry(() =>
  import("../views/crm/WaitlistPage").then((m) => ({ default: m.default })),
);
export const CrmOccupancyTransitionsPage = lazyWithRetry(() =>
  import("../views/crm/OccupancyTransitionsPage").then((m) => ({ default: m.default })),
);

export const CrmYardiResolutionPage = lazyWithRetry(() =>
  import("../views/crm/YardiResolutionPage").then((m) => ({ default: m.default })),
);
export const CrmReportsHubPage = lazyWithRetry(() =>
  import("../views/crm/reports/ReportsHubPage").then((m) => ({ default: m.default })),
);
export const CrmReportPage = lazyWithRetry(() =>
  import("../views/crm/reports/ReportPage").then((m) => ({ default: m.default })),
);
export const CrmReportBuilderPage = lazyWithRetry(() =>
  import("../views/crm/reports/ReportBuilderPage").then((m) => ({ default: m.default })),
);
export const CrmReportSchedulesPage = lazyWithRetry(() =>
  import("../views/crm/reports/ReportSchedulesPage").then((m) => ({ default: m.default })),
);
export const CrmReportDistributionListsPage = lazyWithRetry(() =>
  import("../views/crm/reports/DistributionListsPage").then((m) => ({ default: m.default })),
);
export const CrmReportPackagesPage = lazyWithRetry(() =>
  import("../views/crm/reports/ReportPackagesPage").then((m) => ({ default: m.default })),
);
export const CrmReportPackageEditorPage = lazyWithRetry(() =>
  import("../views/crm/reports/ReportPackageEditorPage").then((m) => ({ default: m.default })),
);
export const CrmEventsPage = lazyWithRetry(() =>
  import("../views/crm/EventsPage").then((m) => ({ default: m.default })),
);
export const CrmHealthDashboardPage = lazyWithRetry(() =>
  import("../views/crm/HealthDashboardPage").then((m) => ({ default: m.default })),
);
export const CrmPersonDetailPage = lazyWithRetry(() =>
  import("../views/crm/PersonDetailPage").then((m) => ({ default: m.default })),
);
export const CrmResidentDetailPage = lazyWithRetry(() =>
  import("../views/crm/ResidentDetailPage").then((m) => ({ default: m.default })),
);
// REF-10 teardown — the org referrer list/detail pages are retired; the
// person-first surfaces below are the referrer experience.
export const CrmReferrerPersonsPage = lazyWithRetry(() =>
  import("../views/crm/ReferrerPersonsPage").then((m) => ({ default: m.default })),
);
// REF-11a — the Organizations browse surface (over the kept org container).
export const CrmOrganizationsPage = lazyWithRetry(() =>
  import("../views/crm/OrganizationsPage").then((m) => ({ default: m.default })),
);
// REF-11b — the org detail.
export const CrmOrganizationDetailPage = lazyWithRetry(() =>
  import("../views/crm/OrganizationDetailPage").then((m) => ({ default: m.default })),
);
export const CrmReferrerPersonDetailPage = lazyWithRetry(() =>
  import("../views/crm/ReferrerPersonDetailPage").then((m) => ({ default: m.default })),
);
export const CrmEventDetailPage = lazyWithRetry(() =>
  import("../views/crm/EventDetailPage").then((m) => ({ default: m.default })),
);
export const CrmCommunityEventsPage = lazyWithRetry(() =>
  import("../views/crm/CommunityEventsPage").then((m) => ({ default: m.default })),
);
export const CrmCommunityEventDetailPage = lazyWithRetry(() =>
  import("../views/crm/CommunityEventDetailPage").then((m) => ({ default: m.default })),
);
export const CrmCommunityEventCreateWizard = lazyWithRetry(() =>
  import("../views/crm/CommunityEventCreateWizard").then((m) => ({ default: m.default })),
);
export const CrmLeadSourcesPage = lazyWithRetry(() =>
  import("../views/crm/settings/LeadSourcesPage").then((m) => ({ default: m.default })),
);
export const CrmApiTokensPage = lazyWithRetry(() =>
  import("../views/crm/settings/ApiTokensPage").then((m) => ({ default: m.default })),
);
export const CrmApiTokenEditPage = lazyWithRetry(() =>
  import("../views/crm/settings/ApiTokenEditPage").then((m) => ({ default: m.default })),
);
export const CrmWebhooksPage = lazyWithRetry(() =>
  import("../views/crm/settings/WebhooksPage").then((m) => ({ default: m.default })),
);
export const CrmWebhookEditPage = lazyWithRetry(() =>
  import("../views/crm/settings/WebhookEditPage").then((m) => ({ default: m.default })),
);
export const CrmGlobalSetupPage = lazyWithRetry(() =>
  import("../views/crm/settings/GlobalSetupPage").then((m) => ({ default: m.default })),
);
export const CrmGlobalSetupRedirect = lazyWithRetry(() =>
  import("../views/crm/settings/GlobalSetupRedirect").then((m) => ({ default: m.default })),
);
export const CrmPacksPage = lazyWithRetry(() =>
  import("../views/crm/settings/PacksPage").then((m) => ({ default: m.default })),
);
export const CrmTerminologyPage = lazyWithRetry(() =>
  import("../views/crm/settings/TerminologyPage").then((m) => ({ default: m.default })),
);
export const CrmCustomFieldsPage = lazyWithRetry(() =>
  import("../views/crm/settings/CustomFieldsPage").then((m) => ({ default: m.default })),
);
export const CrmStagesEditorPage = lazyWithRetry(() =>
  import("../views/crm/settings/StagesEditorPage").then((m) => ({ default: m.default })),
);
export const CrmActivityResultsEditorPage = lazyWithRetry(() =>
  import("../views/crm/settings/ActivityResultsEditorPage").then((m) => ({ default: m.default })),
);
export const CrmActivityTypesEditorPage = lazyWithRetry(() =>
  import("../views/crm/settings/ActivityTypesEditorPage").then((m) => ({ default: m.default })),
);
export const CrmCloseReasonsPage = lazyWithRetry(() =>
  import("../views/crm/settings/CloseReasonsPage").then((m) => ({ default: m.default })),
);
// PLC W15 Inc-5 — Settings → Business hours (the account default week).
export const CrmAccountBusinessHoursPage = lazyWithRetry(() =>
  import("../views/crm/settings/AccountBusinessHoursPage").then((m) => ({ default: m.default })),
);
// PLC W7 Inc-3 — Settings → Competitors.
export const CrmCompetitorsPage = lazyWithRetry(() =>
  import("../views/crm/settings/CompetitorsPage").then((m) => ({ default: m.default })),
);

// H3 Inc-2 — the ADVISOR-side door to the same editor (see the module's header
// for why it re-exports rather than forking). Separate lazy chunk entry so the
// Advisor route does not have to know it lives under views/crm.
// R6 — the Advisor Setup hub.
export const SettingsSetupPage = lazyWithRetry(() =>
  import("../views/settings/setup/SettingsSetupPage").then((m) => ({ default: m.default })),
);

export const SettingsCloseReasonsPage = lazyWithRetry(() =>
  import("../views/settings/closeReasons/CloseReasonsSettingsPage").then((m) => ({
    default: m.default,
  })),
);
// PLC W7 Inc-3 — the Advisor-side door to the same Competitors editor.
export const SettingsCompetitorsPage = lazyWithRetry(() =>
  import("../views/settings/competitors/CompetitorsSettingsPage").then((m) => ({
    default: m.default,
  })),
);
// PACE (`pipeline_funnel`) — the rename-only Stages settings page.
export const SettingsStagesPage = lazyWithRetry(() =>
  import("../views/settings/stages/StagesSettingsPage").then((m) => ({
    default: m.default,
  })),
);
// vertical-lead-profile — Skypoint admins' per-tenant lead-profile overrides.
export const SettingsLeadProfilePage = lazyWithRetry(() =>
  import("../views/settings/leadProfile/LeadProfileSettingsPage").then((m) => ({
    default: m.default,
  })),
);
export const CrmMoveOutReasonsPage = lazyWithRetry(() =>
  import("../views/crm/settings/MoveOutReasonsPage").then((m) => ({ default: m.default })),
);
// PLC W12 Inc-4 — Settings → Stand-up lists.
export const CrmStandupListsPage = lazyWithRetry(() =>
  import("../views/crm/settings/StandupDistributionPage").then((m) => ({ default: m.default })),
);
// PLC Section 1 Inc-4 — Settings → Community groups.
export const CrmCommunityGroupsPage = lazyWithRetry(() =>
  import("../views/crm/settings/CommunityGroupsPage").then((m) => ({ default: m.default })),
);
// PLC W8 Inc-5 — Settings → Lead source groups.
export const CrmLeadSourceGroupsPage = lazyWithRetry(() =>
  import("../views/crm/settings/LeadSourceGroupsPage").then((m) => ({ default: m.default })),
);
// PLC W9 Inc-2 — Settings → Concession types.
export const CrmConcessionTypesPage = lazyWithRetry(() =>
  import("../views/crm/settings/ConcessionTypesPage").then((m) => ({ default: m.default })),
);
// PLC W13 Inc-3 — Global Setup → Discovery → Coaching.
export const CrmDiscoveryCoachSettingsPage = lazyWithRetry(() =>
  import("../views/crm/settings/DiscoveryCoachSettingsPage").then((m) => ({ default: m.default })),
);
export const CrmMotivatorsEditorPage = lazyWithRetry(() =>
  import("../views/crm/settings/MotivatorsEditorPage").then((m) => ({ default: m.default })),
);
export const CrmBarriersEditorPage = lazyWithRetry(() =>
  import("../views/crm/settings/BarriersEditorPage").then((m) => ({ default: m.default })),
);
export const CrmCommitmentTypesEditorPage = lazyWithRetry(() =>
  import("../views/crm/settings/CommitmentTypesEditorPage").then((m) => ({ default: m.default })),
);
export const CrmCareTypesEditorPage = lazyWithRetry(() =>
  import("../views/crm/settings/CareTypesEditorPage").then((m) => ({ default: m.default })),
);
export const CrmCommunitiesListPage = lazyWithRetry(() =>
  import("../views/crm/settings/CommunitiesListPage").then((m) => ({ default: m.default })),
);
export const CrmCommunitySetupPage = lazyWithRetry(() =>
  import("../views/crm/settings/CommunitySetupPage").then((m) => ({ default: m.default })),
);
export const CrmRelationshipsEditorPage = lazyWithRetry(() =>
  import("../views/crm/settings/RelationshipsEditorPage").then((m) => ({ default: m.default })),
);
// Message Templates is rendered inside Communication Templates (imported there with
// `embedded`), not routed standalone. Signature templates were removed; signatures
// are now per-user, edited under My Profile.
export const CrmMyProfilePage = lazyWithRetry(() =>
  import("../views/crm/profile/MyProfilePage").then((m) => ({ default: m.default })),
);
export const CrmCommunicationTemplatesPage = lazyWithRetry(() =>
  import("../views/crm/settings/CommunicationTemplatesPage").then((m) => ({ default: m.default })),
);
export const GoalsSettingsPage = lazyWithRetry(() =>
  import("../views/crm/settings/GoalsSettingsPage").then((m) => ({ default: m.default })),
);
export const CrmSlaConfigPage = lazyWithRetry(() =>
  import("../views/crm/settings/SlaConfigPage").then((m) => ({ default: m.default })),
);
export const CrmScoringConfigPage = lazyWithRetry(() =>
  import("../views/crm/settings/ScoringConfigPage").then((m) => ({ default: m.default })),
);
export const CrmDataExportPage = lazyWithRetry(() =>
  import("../views/crm/settings/DataExportPage").then((m) => ({ default: m.default })),
);
export const CrmDashboardLayoutsPage = lazyWithRetry(() =>
  import("../views/crm/settings/DashboardLayoutsPage").then((m) => ({ default: m.default })),
);
export const CrmAnnouncementsPage = lazyWithRetry(() =>
  import("../views/crm/settings/AnnouncementsPage").then((m) => ({ default: m.default })),
);
export const CrmBiFeedConfigPage = lazyWithRetry(() =>
  import("../views/crm/settings/BiFeedConfigPage").then((m) => ({ default: m.default })),
);
export const CrmDeliveryDestinationsPage = lazyWithRetry(() =>
  import("../views/crm/settings/DeliveryDestinationsPage").then((m) => ({ default: m.default })),
);
export const CrmDeliveryDestinationEditPage = lazyWithRetry(() =>
  import("../views/crm/settings/DeliveryDestinationEditPage").then((m) => ({ default: m.default })),
);
export const CrmAiGovernancePage = lazyWithRetry(() =>
  import("../views/crm/settings/AiGovernancePage").then((m) => ({ default: m.default })),
);
export const CrmAccessLogPage = lazyWithRetry(() =>
  import("../views/crm/settings/AccessLogPage").then((m) => ({ default: m.default })),
);
export const CrmAuditRetentionPage = lazyWithRetry(() =>
  import("../views/crm/settings/AuditRetentionPage").then((m) => ({ default: m.default })),
);
export const CrmConsentEnforcementPage = lazyWithRetry(() =>
  import("../views/crm/settings/ConsentEnforcementPage").then((m) => ({ default: m.default })),
);
export const CrmAutomationsPage = lazyWithRetry(() =>
  import("../views/crm/settings/AutomationsPage").then((m) => ({ default: m.default })),
);
export const CrmClinicalSyncPage = lazyWithRetry(() =>
  import("../views/crm/settings/ClinicalSyncPage").then((m) => ({ default: m.default })),
);
export const CrmMarketingIntegrationsPage = lazyWithRetry(() =>
  import("../views/crm/settings/MarketingIntegrationsPage").then((m) => ({ default: m.default })),
);
